/**
 * Courseware API Service
 * Handles all HTTP communication with the Umbraco backend
 */

import {
  commonUrl,
  getRequest,
  postRequest,
} from "../../../base/services/urlService";
import {
  CoursewareConfigDI,
  CoursewareItemDI,
  CoursewareDetailDI,
  UpdateProgressRequestDI,
  UpdateProgressResponseDI,
} from "../types";

/**
 * Fetch global courseware configuration settings
 * Returns whether authentication is required for courseware access
 */
export const getCoursewareConfig = async (): Promise<CoursewareConfigDI> => {
  try {
    const url = `${commonUrl}/api/courseware/config`;
    
    const token = localStorage.getItem('entraToken') || localStorage.getItem('authToken');
    const headers: Record<string, string> = token ? { 'Authorization': `Bearer ${token}` } : {};
    // console.log('[Courseware Config] Fetching config with token:', token ? 'present' : 'missing');
    
    const response = await getRequest<{ result: CoursewareConfigDI }>(url, {
      headers,
    });
    
    if (response?.result) {
      // console.log('[Courseware Config] Response:', JSON.stringify(response.result));
      return response.result;
    }
    console.warn('[Courseware Config] No result in response, using fallback');
    return { requireAuthentication: false, hasAccess: true };
  } catch (error) {
    console.warn("[Courseware Service] Config endpoint error, using fallback:", error);
    return { requireAuthentication: false, hasAccess: true };
  }
};

/**
 * Get auth headers for courseware API requests
 * Supports both Entra ID and email/password login tokens
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('entraToken') || localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Fetch list of all courseware items
 * Backend returns array directly (no wrapper)
 */
export const getCoursewareList = async (): Promise<CoursewareItemDI[]> => {
  const url = `${commonUrl}/api/courseware`;
  const response = await getRequest<CoursewareItemDI[]>(url, {
    headers: { ...getAuthHeaders() },
  });
  
  return response || [];
};

/**
 * Fetch detailed information for a specific courseware item
 * Includes SCORM data and user-specific progress
 * 
 * @param id - Courseware database ID (NOT Umbraco node ID)
 */
export const getCoursewareDetail = async (
  id: number
): Promise<CoursewareDetailDI> => {
  const url = `${commonUrl}/api/courseware/${id}`;
  const response = await getRequest<CoursewareDetailDI>(url, {
    headers: { ...getAuthHeaders() },
  });
  
  if (!response) {
    throw new Error(`Failed to load courseware ${id}`);
  }
  
  return response;
};

/**
 * Update user's progress for a courseware item
 * Saves SCORM runtime data to backend
 * 
 * @param id - Courseware database ID (NOT Umbraco node ID)
 * @param data - SCORM runtime data to save (PascalCase fields!)
 * @returns Response with updated status
 */
export const updateCoursewareProgress = async (
  id: number,
  data: UpdateProgressRequestDI
): Promise<UpdateProgressResponseDI> => {
  const url = `${commonUrl}/api/courseware/${id}/progress`;
  
  const response = await postRequest<UpdateProgressRequestDI, UpdateProgressResponseDI>(
    url, 
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    }
  );
  
  return response;
};
